import React,{useState} from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import api from './api';
import TokenManager from './services/tokenManager';
import setupTokenInterceptors from './services/axiosInterceptor';
import Sidebar from './components/layout/Sidebar';
import Topbar from './components/layout/Topbar';
import PageContainer from './components/layout/PageContainer';

import StatsSection from './components/StatsSection';
import UsersSection from './components/UsersSection';
import SubjectsSection from './components/SubjectsSection';
import ContextsSection from './components/ContextsSection';
import QuestionsSection from './components/QuestionsSection';
import FrontendUsersSection from './components/FrontendUsersSection';
import ThreadsSection from './components/ThreadsSection';
import ExamsSection from './components/ExamsSection';
import GroupManagerScreen from './components/ui/examSection/GroupManagerScreen';
import PreviousExamsSection from './components/PreviousExamsSection';
import SubjectTopicsSection from './components/SubjectTopicsSection';
import QuestionFormScreen from './components/ui/questionSection/QuestionFormScreen';
import TestQuestionsBySubjectScreen from './components/ui/questionSection/TestQuestionsBySubjectScreen';
import SettingsSection from './components/SettingsSection';


function App(){
  const [token,setToken]=useState(()=>{
    try{
      const saved = localStorage.getItem('adminAuth');
      if(saved){
        const obj = JSON.parse(saved);
        return obj.accessToken || '';
      }
    }catch(e){}
    return '';
  });
  const [refreshToken,setRefreshToken]=useState(()=>{
    try{
      const saved = localStorage.getItem('adminAuth');
      if(saved){
        const obj = JSON.parse(saved);
        return obj.refreshToken || '';
      }
    }catch(e){}
    return '';
  });
  const [error,setError]=useState('');
  const [sideCollapsed,setSideCollapsed] = useState(false);

  React.useEffect(()=>{
    if(token || refreshToken){
      localStorage.setItem('adminAuth', JSON.stringify({accessToken: token, refreshToken}));
      // Initialize token management services after tokens are stored
      TokenManager.setTokens(token, refreshToken);
      setupTokenInterceptors('http://localhost:3000/api', api);
      TokenManager.init(); // Start periodic refresh timer
    } else {
      localStorage.removeItem('adminAuth');
    }
  }, [token, refreshToken]);

  const handleLogout = async () => {
    // Call server logout endpoint (best effort - don't fail if error)
    if(token) {
      try {
        await api.post('/auth/logout', {}, { headers: { 'Authorization': `Bearer ${token}` } });
      } catch(e) {
        // Ignore errors, continue with local logout
        console.error('Server logout error:', e);
      }
    }
    // Clear local tokens
    setToken('');
    setRefreshToken('');
    TokenManager.clear();
  };

  if(!token){
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-10 rounded-xl shadow-2xl w-full max-w-md">
          <h2 className="text-3xl mb-6 text-center font-bold text-indigo-600">Admin Login</h2>
          {error && <p className="text-red-500 mb-4">{error}</p>}
          <form onSubmit={async e=>{
            e.preventDefault();
            setError('');
            try{
              const res=await api.post('/auth/login',{email:e.target.email.value,password:e.target.password.value,admin:true});
              setToken(res.data.accessToken);
              setRefreshToken(res.data.refreshToken);
            }catch(err){setError(err.response?.data?.error||'Login failed');}
          }} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input name="email" type="text" className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 shadow-sm focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <input name="password" type="password" className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 shadow-sm focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
            <button type="submit" className="w-full bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 transition">Login</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <AppInner
        token={token}
        refreshToken={refreshToken}
        sideCollapsed={sideCollapsed}
        setSideCollapsed={setSideCollapsed}
        onLogout={handleLogout}
        error={error}
        setError={setError}
      />
    </Router>
  );
}

function AppInner({ token, refreshToken, sideCollapsed, setSideCollapsed, onLogout, error, setError }) {
  const navigate = useNavigate();

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar token={token} collapsed={sideCollapsed} onToggle={()=>setSideCollapsed(c=>!c)} />
      <div className="flex flex-col flex-1">
        <Topbar onMenuClick={()=>setSideCollapsed(c=>!c)} onSettings={()=>navigate('/settings')} onLogout={onLogout} />
        <PageContainer>
            {error && <p className="text-red-500 mb-4">{error}</p>}
            <Routes>
              <Route path="/stats" element={<StatsSection token={token} setError={setError} />} />
              <Route path="/users" element={<UsersSection token={token} setError={setError} />} />
              <Route path="/subjects" element={<SubjectsSection token={token} setError={setError} />} />
              <Route path="/subjects/:subjectId/topics" element={<SubjectTopicsSection token={token} setError={setError} />} />
              <Route path="/contexts" element={<ContextsSection token={token} setError={setError} />} />
              <Route path="/questions" element={<QuestionsSection token={token} setError={setError} />} />
              <Route path="/questions/test/:testId" element={<TestQuestionsBySubjectScreen token={token} setError={setError} />} />
              <Route path="/questions/add" element={<QuestionFormScreen token={token} setError={setError} />} />
              <Route path="/questions/edit/:questionId" element={<QuestionFormScreen token={token} setError={setError} />} />
              <Route path="/exams" element={<ExamsSection token={token} setError={setError} />} />
              <Route path="/exams/groups/:testId" element={<GroupManagerScreen token={token} setError={setError} />} />
              <Route path="/previous-exams" element={<PreviousExamsSection token={token} setError={setError} />} />
              <Route path="/frontendUsers" element={<FrontendUsersSection token={token} setError={setError} />} />
              <Route path="/threads" element={<ThreadsSection token={token} setError={setError} />} />
              <Route path="/settings" element={<SettingsSection token={token} setError={setError} />} />
              <Route path="*" element={<Navigate to="/stats" replace />} />
            </Routes>
          </PageContainer>
        </div>
      </div>
  );
}

export default App;
